import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Upload, ImageIcon, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { isMissingOrInvalidSwissVat } from "@/components/VatNumberPrompt";
import ClosuresEditor from "@/components/ClosuresEditor";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const CANTONS = ["VD","GE","FR","NE","JU","VS","BE","ZH","BS","BL","AG","SO","SG","TI","LU","ZG","SH","TG","GR","UR","SZ","OW","NW","GL","AR","AI"];
const STEPS = ["s_restaurant", "s_closures", "s_vat", "s_fiduciary"];
const Label = ({ children }) => <label className="text-sm font-medium text-text-secondary block mb-2">{children}</label>;

function VatHint({ vat }) {
  const { t } = useTranslation();
  if (!vat) return null;
  const bad = isMissingOrInvalidSwissVat(vat);
  return (
    <p className={`text-[11px] mt-1.5 flex items-center gap-1 ${bad ? "text-amber-700" : "text-primary-c"}`} data-testid="vat-validation-hint">
      {bad ? <AlertCircle className="w-3.5 h-3.5 shrink-0" /> : <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />}
      <span>{bad ? <>{t("onb.vat_expected")} <strong>{t("onb.vat_example")}</strong></> : t("onb.vat_valid")}</span>
    </p>
  );
}

export default function Onboarding() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: "", address: "", canton: "VD", vat_number: "", covers_per_day: "", logo_data_url: null, vat_method: "effective", tdfn_rate_1: 5.3, tdfn_rate_2: 0.6, fiduciary_email: "", closed_weekdays: [], closures: [] });
  const [loading, setLoading] = useState(false);
  const [addrKey, setAddrKey] = useState(0);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    const e = user?.establishment;
    if (e) { setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(e).filter(([k, v]) => k in p && v !== null && v !== undefined)) })); setAddrKey((x) => x + 1); }
    else if (user?.restaurant_name) setF((p) => ({ ...p, name: p.name || user.restaurant_name }));
  }, [user]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t("onb.logo_too_big")); return; }
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    setLoading(true);
    try {
      await api.post("/establishment", { ...f, tdfn_rate_1: Number(f.tdfn_rate_1) || null, tdfn_rate_2: Number(f.tdfn_rate_2) || null });
      await refresh();
      toast.success(t("onb.saved"));
      navigate("/app/accueil");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg p-6">
      <div className="max-w-md mx-auto page-enter">
        <h1 className="font-display text-3xl">{t("onb.title_a")} <span className="font-serif-italic text-primary-c">{t("onb.title_b")}</span></h1>
        <p className="text-text-secondary text-sm mt-1">{t("onb.subtitle")}</p>
        <ol className="flex gap-2 mt-5" data-testid="onb-steps">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex-1 text-center text-[11px] font-semibold py-1.5 rounded-full ${i <= step ? "bg-primary-c text-white" : "bg-surface-muted text-text-secondary"}`}>{i + 1}. {t(`onb.${s}`)}</li>
          ))}
        </ol>

        <form onSubmit={onSubmit} className="card-c mt-4 space-y-4">
          {step === 0 && (
            <>
              <div><Label>{t("onb.name")}</Label><input data-testid="onb-name-input" required value={f.name} onChange={set("name")} className="input-c" placeholder={t("onb.name_ph")} /></div>
              <div><Label>{t("address.label")}</Label><AddressAutocomplete key={addrKey} value={f.address} onChange={(x) => setF((p) => ({ ...p, ...x }))} testPrefix="onb-address" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("onb.canton")}</Label><select data-testid="onb-canton-select" value={f.canton} onChange={set("canton")} className="input-c">{CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><Label>{t("onb.covers")}</Label>
                  <select data-testid="onb-covers-select" value={f.covers_per_day} onChange={set("covers_per_day")} className="input-c">
                    <option value="">—</option><option value="<30">{t("onb.c_lt30")}</option><option value="30-60">{t("onb.c_30_60")}</option><option value="60-120">{t("onb.c_60_120")}</option><option value=">120">{t("onb.c_gt120")}</option>
                  </select>
                </div>
              </div>
              <div><Label>{t("onb.logo")}</Label>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-surface-muted border border-c flex items-center justify-center overflow-hidden">
                    {f.logo_data_url ? <img src={f.logo_data_url} alt="Logo" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-text-secondary" strokeWidth={1.5} />}
                  </div>
                  <button type="button" data-testid="onb-upload-logo-button" onClick={() => fileRef.current?.click()} className="btn-secondary flex items-center gap-2"><Upload className="w-4 h-4" />{t("onb.choose")}</button>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <p className="text-xs text-text-secondary" data-testid="onb-closures-optional">{t("onb.closures_optional")}</p>
              <ClosuresEditor weekdays={f.closed_weekdays || []} closures={f.closures || []} onChange={(v) => setF((p) => ({ ...p, ...v }))} />
            </>
          )}

          {step === 2 && (
            <>
              <div><Label>{t("onb.vat_number")}</Label><input data-testid="onb-vat-input" value={f.vat_number} onChange={set("vat_number")} className="input-c" placeholder={t("onb.vat_example")} /><VatHint vat={f.vat_number} /></div>
              <div><Label>{t("onb.vat_method")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[["effective", t("onb.m_effective"), t("onb.m_effective_d")], ["tdfn", t("onb.m_tdfn"), t("onb.m_tdfn_d")]].map(([v, l, d]) => (
                    <button type="button" key={v} onClick={() => setF((p) => ({ ...p, vat_method: v }))} data-testid={`onb-vat-method-${v}`} className={`p-3 rounded-xl text-left border transition-colors ${f.vat_method === v ? "border-primary-c bg-primary-c/10" : "border-c bg-white"}`}>
                      <p className="font-semibold text-sm">{l}</p><p className="text-[11px] text-text-secondary mt-0.5">{d}</p>
                    </button>
                  ))}
                </div>
              </div>
              {f.vat_method === "tdfn" && (
                <div className="grid grid-cols-2 gap-3" data-testid="onb-tdfn-rates">
                  <div><Label>{t("onb.rate1")}</Label><input type="number" step="0.1" min="0" max="10" data-testid="onb-tdfn-rate-1" value={f.tdfn_rate_1 ?? ""} onChange={set("tdfn_rate_1")} className="input-c" /></div>
                  <div><Label>{t("onb.rate2")}</Label><input type="number" step="0.1" min="0" max="10" data-testid="onb-tdfn-rate-2" value={f.tdfn_rate_2 ?? ""} onChange={set("tdfn_rate_2")} className="input-c" /></div>
                  <p className="col-span-2 text-[11px] text-text-secondary -mt-2">{t("onb.rates_hint")}</p>
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <div>
              <Label>{t("onb.fid_email")}</Label>
              <input type="email" data-testid="onb-fiduciary-email" value={f.fiduciary_email || ""} onChange={set("fiduciary_email")} className="input-c" placeholder={t("onb.fid_ph")} />
              <p className="text-[11px] text-text-secondary mt-1.5">{t("onb.fid_hint")}</p>
            </div>
          )}

          <div className="flex gap-2">
            {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="btn-secondary flex items-center gap-1" data-testid="onb-back-button" aria-label={t("onb.back")}><ArrowLeft className="w-4 h-4" /></button>}
            <button data-testid={step < STEPS.length - 1 ? "onb-next-button" : "onb-submit-button"} disabled={loading} className="btn-primary flex-1">
              {loading ? t("onb.saving") : step < STEPS.length - 1 ? t("onb.continue") : t("onb.finish")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
